import { useState } from 'react';
import { tx } from '../i18n';
import { idioma } from '../i18n/idioma';
import { pedir } from '../lib/api';
import { CANAL, permisos } from '../lib/appliance';
import { useConsulta } from '../lib/consulta';
import { cuando, gigas, miles } from '../lib/formato';
import { useSesion } from '../sesion/sesion';
import { Cabecera, Consulta, Pastilla, primero, useAviso, useRefrescar, Ventana as Modal } from '../ui/piezas';

/** Un paquete que está en el blob (armado y firmado) y todavía no se publicó. */
interface Disponible {
  numero: string;
  bytes: number;
  cuando: string | null;
  imagenes: { formato: string; bytes: number }[];
  /** Las que dejó el empaquetador al lado del paquete; null = escribirlas a mano. */
  notas?: { es: string; en: string } | null;
}

interface Version {
  numero: string;
  canal: 'estable' | 'beta';
  notas_en: string;
  notas_es: string;
  paquete: string | null;
  firma: string | null;
  minima_desde: string | null;
  publicada: string;
  quien: string;
  retirada: string | null;
  appliances: number;
  /**
   * Qué imágenes de VM hay para esta versión (lo dice el storage, no una lista). Opcional a
   * propósito: una API más vieja no manda el campo, y eso no puede tumbar la pantalla.
   */
  imagenes?: { formato: string; bytes: number; cuando: string | null }[];
  /** Si se le puede aplicar a un appliance: hay paquete, anotado o en el blob. */
  aplicable?: boolean;
  /** El paquete está en el blob: de ahí se arma la imagen de VM. */
  paquete_en_blob?: boolean;
  /** Cómo va (o cómo terminó) el armado de su imagen de VM. */
  construccion?: { estado: 'pedida' | 'armando' | 'listo' | 'error'; paso: string; desde: string; cuando: string } | null;
  /** Si este Centro tiene el constructor de imágenes. */
  puede_armar?: boolean;
}

/**
 * El catálogo de versiones y el parque (docs/centro.md §5). Publicar una versión no toca
 * ningún appliance: después se decide a quién se le pide que se actualice, y cada uno lo
 * hace en su ventana de mantenimiento.
 */
export function Versiones() {
  const { usuario } = useSesion();
  const puede = permisos(usuario?.rol);
  // mientras se arma una imagen, cada 10 s: se ve el paso y aparecen las descargas al terminar
  const q = useConsulta<Version[]>('/versiones', {
    refetchInterval: (x) => ((x.state.data ?? []).some((v) => v.construccion?.estado === 'pedida' || v.construccion?.estado === 'armando') ? 10_000 : 120_000),
  });
  const parque = useConsulta<{ version: string | null; appliances: number }[]>('/versiones/parque', { refetchInterval: 120_000 });
  const refrescar = useRefrescar();
  const [aviso, avisar] = useAviso();
  // los paquetes que ya están en el blob y no se publicaron: se publican de ahí
  const disponibles = useConsulta<Disponible[]>('/versiones/disponibles', { enabled: puede.publicarVersion, refetchInterval: 120_000 });
  const [publicar, setPublicar] = useState<string | null | false>(false);
  const [mover, setMover] = useState<Version | null>(null);
  const [retirar, setRetirar] = useState<Version | null>(null);
  const [borrar, setBorrar] = useState<Version | null>(null);
  // la más nueva no se borra: es la que se baja para instalar un cliente nuevo
  const laNueva = (q.data ?? [])
    .map((v) => v.numero)
    .sort((a, b) => {
      const x = a.split('.').map(Number);
      const y = b.split('.').map(Number);
      return x[0] - y[0] || x[1] - y[1] || x[2] - y[2];
    })
    .at(-1);

  return (
    <div className="pagina">
      <Cabecera
        titulo={tx.versiones.titulo}
        accion={
          // con paquetes listos se publica desde su fila; este queda para uno que está en otro lado
          puede.publicarVersion &&
          disponibles.isSuccess &&
          !disponibles.data.length && (
            <button type="button" className="btn" onClick={() => setPublicar(null)}>
              {tx.versiones.publicar}
            </button>
          )
        }
      >
        {tx.versiones.que}
      </Cabecera>
      {aviso}

      {puede.publicarVersion && (disponibles.data ?? []).length > 0 && (
        <section className="panel listas-publicar">
          <h2 className="rotulo">{tx.versiones.listas}</h2>
          <p className="nota">{tx.versiones.listasQue}</p>
          <ul className="disponibles">
            {(disponibles.data ?? []).map((d) => (
              <li key={d.numero}>
                <b className="num">{d.numero}</b>
                <span className="nota">{tx.versiones.paqueteDe(gigas(d.bytes), d.cuando ? cuando(d.cuando) : '—')}</span>
                {d.imagenes.length > 0 && (
                  <span className="nota">{d.imagenes.map((i) => tx.versiones.formatos[i.formato] ?? i.formato).join(' · ')}</span>
                )}
                <button type="button" className="btn prim" onClick={() => setPublicar(d.numero)}>
                  {tx.versiones.publicarEsta(d.numero)}
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* con qué anda la flota: una franja chica, no un panel casi vacío */}
      <Consulta q={parque}>
        {(parque.data ?? []).length > 0 && (
          <p className="flota-ahora">
            <span className="rotulo">{tx.versiones.parque}</span>
            {(parque.data ?? []).map((p) => (
              <span key={p.version ?? 'sin'} className="flota-version">
                <b>{p.version ?? tx.comun.noReportada}</b> {tx.versiones.enAppliances(p.appliances)}
              </span>
            ))}
          </p>
        )}
      </Consulta>

      <section className="catalogo">
        <h2 className="rotulo">{tx.versiones.catalogo}</h2>
        <Consulta q={q} vacio={tx.versiones.sinVersiones}>
          <div className="versiones-lista">
            {(q.data ?? []).map((v) => (
              <TarjetaVersion
                key={v.numero}
                v={v}
                puede={puede.publicarVersion}
                esLaNueva={v.numero === laNueva}
                alRepartir={() => setMover(v)}
                alRetirar={() => setRetirar(v)}
                alBorrar={() => setBorrar(v)}
                alArmar={() =>
                  void pedir(`/versiones/${v.numero}/imagen`, { metodo: 'POST' })
                    .then(() => {
                      avisar(tx.versiones.armandoOk(v.numero));
                      void refrescar('/versiones');
                    })
                    .catch((e: Error) => avisar(e.message, true))
                }
              />
            ))}
          </div>
        </Consulta>
      </section>

      {borrar && (
        <Modal
          titulo={tx.versiones.borrarTitulo(borrar.numero)}
          boton={tx.versiones.borrar}
          peligro
          alCerrar={() => setBorrar(null)}
          alEnviar={async () => {
            await pedir(`/versiones/${borrar.numero}`, { metodo: 'DELETE' });
            void refrescar('/versiones');
            avisar(tx.versiones.borradaOk(borrar.numero));
            setBorrar(null);
          }}
        >
          <p className="nota">{tx.versiones.borrarQue}</p>
        </Modal>
      )}

      {retirar && (
        <Modal
          titulo={tx.versiones.retirarTitulo(retirar.numero)}
          boton={tx.versiones.retirar}
          peligro
          alCerrar={() => setRetirar(null)}
          alEnviar={async () => {
            await pedir(`/versiones/${retirar.numero}/retirar`, { metodo: 'POST' });
            void refrescar('/versiones');
            avisar(tx.versiones.retiradaOk(retirar.numero));
            setRetirar(null);
          }}
        >
          <p className="nota">{tx.versiones.retirarQue}</p>
        </Modal>
      )}

      {publicar !== false && (
        <PublicarVersion
          inicial={publicar}
          disponibles={disponibles.data ?? []}
          alCerrar={() => setPublicar(false)}
          alPublicar={(n) => {
            void refrescar('/versiones');
            setPublicar(false);
            avisar(tx.versiones.publicadaOk(n));
          }}
        />
      )}

      {mover && (
        <Modal
          titulo={tx.versiones.repartirTitulo(mover.numero)}
          boton={tx.versiones.repartirBoton}
          alCerrar={() => setMover(null)}
          alEnviar={async () => {
            const r = await pedir<{ pedidas: number; saltadas: number }>('/versiones/actualizar-canal', {
              metodo: 'POST',
              cuerpo: { canal: mover.canal, version: mover.numero },
            });
            setMover(null);
            avisar(tx.versiones.repartido(r.pedidas, r.saltadas));
          }}
        >
          <p>{tx.versiones.repartirQue(CANAL[mover.canal] ?? mover.canal, mover.numero)}</p>
          <p className="nota">{tx.versiones.repartirNota}</p>
        </Modal>
      )}
    </div>
  );
}

/**
 * Una versión del catálogo: a la izquierda qué es (número, canal, cuándo, cuántos la tienen),
 * al centro qué trae —en puntos, las primeras a la vista— y a la derecha qué se hace con ella.
 */
function TarjetaVersion({
  v,
  puede,
  esLaNueva,
  alRepartir,
  alRetirar,
  alBorrar,
  alArmar,
}: {
  v: Version;
  puede: boolean;
  esLaNueva: boolean;
  alRepartir: () => void;
  alRetirar: () => void;
  alBorrar: () => void;
  alArmar: () => void;
}) {
  // por qué no se puede borrar (se dice al pasar el mouse); null = se puede
  const noBorrar = v.appliances > 0 ? tx.versiones.noBorrarEnUso(v.appliances) : esLaNueva ? tx.versiones.noBorrarNueva : null;
  const [todas, setTodas] = useState(false);
  // En el idioma de quien mira; si esa no se escribió, la otra antes que nada.
  const notas = primero(idioma() === 'es' ? v.notas_es : v.notas_en, v.notas_en, v.notas_es, '') || '';
  const partes = notas
    .split(/\n|\s•\s|^•\s/)
    .map((x) => x.replace(/^•\s*/, '').trim())
    .filter(Boolean);
  const [intro, ...puntos] = partes.length > 1 && !notas.trimStart().startsWith('•') ? partes : ['', ...partes];
  const visibles = todas ? puntos : puntos.slice(0, 3);
  return (
    <article className={`version${v.retirada ? ' retirada' : ''}`}>
      <div className="version-que">
        <b className="version-num">{v.numero}</b>
        <Pastilla tono={v.retirada ? 'caido' : v.canal === 'beta' ? 'parcial' : 'arriba'}>
          {v.retirada ? tx.versiones.retirada : (CANAL[v.canal] ?? v.canal)}
        </Pastilla>
        <small>{cuando(v.publicada)}</small>
        <small className="tenue">{v.quien}</small>
        <small>
          <b>{miles(v.appliances)}</b> {tx.versiones.laTienen(v.appliances)}
        </small>
        {v.minima_desde && <small className="tenue">{tx.versiones.necesita(v.minima_desde)}</small>}
        {v.aplicable === false && <small className="tenue">{tx.versiones.sinPaquete}</small>}
      </div>
      <div className="version-notas">
        {intro && <p>{intro}</p>}
        {visibles.length > 0 && (
          <ul>
            {visibles.map((x, i) => (
              <li key={i}>{x}</li>
            ))}
          </ul>
        )}
        {puntos.length > 3 && (
          <button type="button" className="btn fantasma chico" onClick={() => setTodas(!todas)}>
            {todas ? tx.versiones.verMenos : tx.versiones.verTodas(puntos.length)}
          </button>
        )}
        {!intro && !visibles.length && <p className="tenue">—</p>}
      </div>
      <div className="version-acc">
        {puede && (
          <div className="version-botones">
            {!v.retirada && v.aplicable !== false && (
              <button type="button" className="btn contorno" onClick={alRepartir}>
                {tx.versiones.repartir}
              </button>
            )}
            {!v.retirada && (
              <button type="button" className="btn peligro" onClick={alRetirar}>
                {tx.versiones.retirar}
              </button>
            )}
            <button type="button" className="btn peligro" disabled={!!noBorrar} title={noBorrar ?? undefined} onClick={alBorrar}>
              {tx.versiones.borrar}
            </button>
          </div>
        )}
        {(() => {
          const c = v.construccion;
          const armando = c?.estado === 'pedida' || c?.estado === 'armando';
          if (armando)
            return (
              <p className="version-armando" role="status">
                <span className="gira" aria-hidden="true" />
                {tx.versiones.armando(c!.paso)}
              </p>
            );
          return (
            <>
              {c?.estado === 'error' && <p className="alerta version-armando">{tx.versiones.armadoFallo(c.paso)}</p>}
              {puede && v.puede_armar && v.paquete_en_blob && !v.retirada && (
                <button type="button" className="btn chico" onClick={alArmar}>
                  {(v.imagenes ?? []).length ? tx.versiones.rearmar : tx.versiones.armar}
                </button>
              )}
            </>
          );
        })()}
        {(v.imagenes ?? []).length > 0 && (
          <div className="version-imagenes">
            <small className="tenue">{tx.versiones.imagen}</small>
            {(v.imagenes ?? []).map((i) => (
              // Un enlace y no un botón: el navegador la baja del storage directo, con un
              // enlace firmado que dura cinco minutos.
              <a
                key={i.formato}
                className="descarga"
                href={`/api/versiones/${v.numero}/imagen/${i.formato}`}
                download
                title={i.cuando ? tx.versiones.imagenDel(cuando(i.cuando)) : undefined}
              >
                {tx.versiones.formatos[i.formato] ?? i.formato}
                <small>{gigas(i.bytes)}</small>
              </a>
            ))}
          </div>
        )}
      </div>
    </article>
  );
}

function PublicarVersion({
  inicial,
  disponibles,
  alCerrar,
  alPublicar,
}: {
  inicial: string | null;
  disponibles: Disponible[];
  alCerrar: () => void;
  alPublicar: (numero: string) => void;
}) {
  // de un paquete del blob (lo normal) o, avanzado, de uno que está en otro lado
  const [numero, setNumero] = useState(inicial ?? '');
  const [afuera, setAfuera] = useState(!disponibles.length);
  const [canal, setCanal] = useState<'estable' | 'beta'>('beta');
  // las notas vienen con el paquete: se revisan, no se escriben
  const notasDe = (n: string) => disponibles.find((d) => d.numero === n)?.notas ?? null;
  const [notasEn, setNotasEn] = useState(() => notasDe(inicial ?? '')?.en ?? '');
  const [notasEs, setNotasEs] = useState(() => notasDe(inicial ?? '')?.es ?? '');
  const [paquete, setPaquete] = useState('');
  const [firma, setFirma] = useState('');
  const [minima, setMinima] = useState('');
  const elegido = disponibles.find((d) => d.numero === numero);
  return (
    <Modal
      titulo={tx.versiones.publicarTitulo(numero || '…')}
      boton={tx.versiones.publicarEsta(numero || '…')}
      alCerrar={alCerrar}
      alEnviar={async () => {
        const falta = primero(
          !/^\d+\.\d+\.\d+$/.test(numero.trim()) && tx.versiones.formatoNumero,
          !notasEs.trim() && tx.versiones.faltaNotasEs,
          !notasEn.trim() && tx.versiones.faltaNotasEn,
          afuera && !paquete.trim() && tx.versiones.faltaPaquete,
        );
        if (falta) return falta;
        await pedir('/versiones', {
          metodo: 'POST',
          cuerpo: {
            numero: numero.trim(),
            canal,
            notas_en: notasEn.trim(),
            notas_es: notasEs.trim(),
            paquete: afuera ? paquete.trim() || undefined : undefined,
            firma: afuera ? firma.trim() || undefined : undefined,
            minima_desde: minima.trim() || undefined,
          },
        });
        alPublicar(numero.trim());
      }}
    >
      <div className="pv-form">
        <div className="pv-fila">
          <label className="campo" htmlFor="v-num">
            {tx.versiones.numero}
            {afuera ? (
              <input
                id="v-num"
                className="entrada"
                value={numero}
                onChange={(e) => setNumero(e.target.value)}
                placeholder={tx.versiones.numeroEjemplo}
              />
            ) : (
              <select
                id="v-num"
                className="lista-sel"
                value={numero}
                onChange={(e) => {
                  setNumero(e.target.value);
                  const n = notasDe(e.target.value);
                  if (n) {
                    setNotasEs(n.es);
                    setNotasEn(n.en);
                  }
                }}
              >
                {disponibles.map((d) => (
                  <option key={d.numero} value={d.numero}>
                    {d.numero}
                  </option>
                ))}
              </select>
            )}
            {!afuera && elegido && (
              <small className="ayuda">{tx.versiones.paqueteDe(gigas(elegido.bytes), elegido.cuando ? cuando(elegido.cuando) : '—')}</small>
            )}
          </label>
          <label className="campo" htmlFor="v-canal">
            {tx.versiones.canal}
            <select id="v-canal" className="lista-sel" value={canal} onChange={(e) => setCanal(e.target.value as typeof canal)}>
              <option value="beta">{tx.comun.canales.beta}</option>
              <option value="estable">{tx.comun.canales.estable}</option>
            </select>
            <small className="ayuda">{canal === 'beta' ? tx.versiones.canalBeta : tx.versiones.canalEstable}</small>
          </label>
          <label className="campo" htmlFor="v-min">
            {tx.versiones.minima}
            <input
              id="v-min"
              className="entrada"
              value={minima}
              onChange={(e) => setMinima(e.target.value)}
              placeholder={tx.versiones.minimaEjemplo}
            />
            <small className="ayuda">{tx.versiones.minimaAyuda}</small>
          </label>
        </div>
        <div className="pv-fila dos">
          <label className="campo" htmlFor="v-es">
            {tx.versiones.queCambioEs}
            <textarea id="v-es" className="entrada" rows={9} value={notasEs} onChange={(e) => setNotasEs(e.target.value)} />
          </label>
          <label className="campo" htmlFor="v-en">
            {tx.versiones.queCambioEn}
            <textarea id="v-en" className="entrada" rows={9} value={notasEn} onChange={(e) => setNotasEn(e.target.value)} />
          </label>
        </div>
        {!afuera && notasDe(numero) && <p className="nota">{tx.versiones.notasDelPaquete}</p>}
        <label className="pv-afuera">
          <input
            type="checkbox"
            checked={afuera}
            onChange={(e) => {
              setAfuera(e.target.checked);
              if (!e.target.checked) setNumero(disponibles[0]?.numero ?? '');
            }}
            disabled={!disponibles.length}
          />
          {tx.versiones.afuera}
        </label>
        {afuera && (
          <div className="pv-fila dos">
            <label className="campo" htmlFor="v-paq">
              {tx.versiones.paquete}
              <input id="v-paq" className="entrada" value={paquete} onChange={(e) => setPaquete(e.target.value)} placeholder="https://…" />
              <small className="ayuda">{tx.versiones.paqueteAyuda}</small>
            </label>
            <label className="campo" htmlFor="v-firma">
              {tx.versiones.firma}
              <input id="v-firma" className="entrada" value={firma} onChange={(e) => setFirma(e.target.value)} />
              <small className="ayuda">{tx.versiones.firmaAyuda}</small>
            </label>
          </div>
        )}
      </div>
    </Modal>
  );
}
